import { useRef } from 'react';
import { util as fabricUtil, Group } from 'fabric';
import { CreateObjectCommand } from '../commands/CreateObjectCommand';
import { DeleteObjectCommand } from '../commands/DeleteObjectCommand';
import { ReorderObjectCommand } from '../commands/ReorderObjectCommand';
import { CompoundCommand } from '../commands/CompoundCommand';
import { UngroupObjectsCommand } from '../commands/UngroupObjectsCommand';
import { applyLayerOrder } from '../constants/layerDefinitions';
import { PlantCircle } from '../components/Editor/objects/PlantCircle';
import { sanitizeClonedObjectData } from '../components/Editor/tools/gateLinkUtils';

/**
 * useContextMenuActions - Hook providing all canvas context menu action callbacks.
 *
 * Handles: copy, paste, duplicate, bringToFront, sendToBack, group, ungroup, deleteSelected.
 *
 * All mutating actions are wrapped in Commands for undo/redo support.
 *
 * @param {React.RefObject<fabric.Canvas>} canvasRef - Ref to Fabric.js canvas instance
 * @param {Object} commandStack - CommandStack from CommandContext
 * @returns {Object} Action callbacks
 */
function useContextMenuActions(canvasRef, commandStack) {
  // In-memory clipboard: { objects: Array<Object> (JSON), sourcePositions: Array<{left, top}> }
  const clipboardRef = useRef(null);

  /**
   * Copy the current selection to the in-memory clipboard.
   * Works for single objects and ActiveSelection (multi-select).
   * @returns {boolean} true if something was copied
   */
  const copy = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const active = canvas.getActiveObject();
    if (!active) return false;

    if (active.type === 'activeselection') {
      const objects = active.getObjects();
      clipboardRef.current = {
        objects: objects.map((obj) => obj.toObject(['data', 'objectType', 'renderOptions', 'plantProperties'])),
        sourcePositions: objects.map((obj) => ({
          left: obj.left + (active.left || 0) + (active.width || 0) / 2,
          top: obj.top + (active.top || 0) + (active.height || 0) / 2,
        })),
      };
    } else {
      clipboardRef.current = {
        objects: [active.toObject(['data', 'objectType', 'renderOptions', 'plantProperties', 'selectedColor', 'isOverlapping', 'overlapState'])],
        sourcePositions: [{ left: active.left, top: active.top }],
      };
    }

    return true;
  };

  /**
   * Paste from clipboard. Creates new objects with an offset.
   * @param {{ x: number, y: number }|null} mousePosition - If provided, paste at mouse position (right-click paste)
   * @returns {boolean} true if paste succeeded
   */
  const paste = async (mousePosition = null) => {
    const canvas = canvasRef.current;
    if (!canvas || !clipboardRef.current) return false;

    const { objects, sourcePositions } = clipboardRef.current;
    const OFFSET = 20;

    const enlivened = await fabricUtil.enlivenObjects(objects);

    const commands = enlivened.map((obj, i) => {
      const srcPos = sourcePositions[i];
      const savedData = objects[i];

      // enlivenObjects doesn't know about PlantCircle — reconstruct it manually
      let finalObj = obj;
      if (savedData.objectType === 'plant' && savedData.plantProperties) {
        finalObj = PlantCircle.fromObject(savedData);
      }

      if (mousePosition) {
        // Paste at right-click position (offset from center of copied group)
        const centerX = sourcePositions.reduce((sum, p) => sum + p.left, 0) / sourcePositions.length;
        const centerY = sourcePositions.reduce((sum, p) => sum + p.top, 0) / sourcePositions.length;
        finalObj.set({
          left: mousePosition.x + (srcPos.left - centerX),
          top: mousePosition.y + (srcPos.top - centerY),
        });
      } else {
        // Standard paste: offset by +20px
        finalObj.set({
          left: srcPos.left + OFFSET,
          top: srcPos.top + OFFSET,
        });
      }

      // Restore extra properties for non-plant objects
      if (savedData.data) finalObj.data = { ...savedData.data };
      if (savedData.objectType && finalObj.objectType !== savedData.objectType) finalObj.objectType = savedData.objectType;
      if (savedData.renderOptions) finalObj.renderOptions = savedData.renderOptions;

      // Fresh objectId for structures, drop gate links — pasted copies start unlinked (#611)
      sanitizeClonedObjectData(finalObj);

      finalObj.setCoords();
      return new CreateObjectCommand(canvas, finalObj);
    });

    if (commands.length === 1) {
      commandStack.execute(commands[0]);
    } else {
      commandStack.execute(new CompoundCommand(commands, 'Einfügen'));
    }

    // Update clipboard positions so repeated pastes cascade
    clipboardRef.current = {
      objects,
      sourcePositions: sourcePositions.map((p) => ({
        left: p.left + OFFSET,
        top: p.top + OFFSET,
      })),
    };

    return true;
  };

  /**
   * Duplicate: copy + immediate paste with +20px offset.
   * @returns {boolean} true if duplicated
   */
  const duplicate = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const active = canvas.getActiveObject();
    if (!active) return false;

    const OFFSET = 20;

    let objectsToCopy;
    let sourcePositions;

    const EXTRA_PROPS = ['data', 'objectType', 'renderOptions', 'plantProperties', 'selectedColor', 'isOverlapping', 'overlapState'];
    if (active.type === 'activeselection') {
      const objs = active.getObjects();
      objectsToCopy = objs.map((obj) => obj.toObject(EXTRA_PROPS));
      sourcePositions = objs.map((obj) => ({
        left: obj.left + (active.left || 0) + (active.width || 0) / 2,
        top: obj.top + (active.top || 0) + (active.height || 0) / 2,
      }));
    } else {
      objectsToCopy = [active.toObject(EXTRA_PROPS)];
      sourcePositions = [{ left: active.left, top: active.top }];
    }

    const enlivened = await fabricUtil.enlivenObjects(objectsToCopy);

    const commands = enlivened.map((obj, i) => {
      const savedData = objectsToCopy[i];

      // enlivenObjects doesn't know about PlantCircle — reconstruct it manually
      let finalObj = obj;
      if (savedData.objectType === 'plant' && savedData.plantProperties) {
        finalObj = PlantCircle.fromObject(savedData);
      }

      finalObj.set({
        left: sourcePositions[i].left + OFFSET,
        top: sourcePositions[i].top + OFFSET,
      });

      if (savedData.data) finalObj.data = { ...savedData.data };
      if (savedData.objectType && finalObj.objectType !== savedData.objectType) finalObj.objectType = savedData.objectType;
      if (savedData.renderOptions) finalObj.renderOptions = savedData.renderOptions;

      // Fresh objectId for structures, drop gate links — duplicates start unlinked (#611)
      sanitizeClonedObjectData(finalObj);

      finalObj.setCoords();
      return new CreateObjectCommand(canvas, finalObj);
    });

    if (commands.length === 1) {
      commandStack.execute(commands[0]);
    } else {
      commandStack.execute(new CompoundCommand(commands, 'Duplizieren'));
    }

    return true;
  };

  /**
   * Bring the selected object(s) to front within their layer.
   */
  const bringToFront = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const active = canvas.getActiveObject();
    if (!active) return;

    const targets = active.type === 'activeselection'
      ? active.getObjects()
      : [active];

    const nonGridObjects = canvas.getObjects().filter(
      (o) => !o.data?.isGridLine && !o.data?.isSnapGuide
    );
    const maxZ = nonGridObjects.reduce(
      (max, o) => Math.max(max, o.data?.zIndexWithinLayer ?? 0),
      -1
    );

    const changes = targets.map((obj, i) => ({
      object: obj,
      oldZIndex: obj.data?.zIndexWithinLayer ?? 0,
      newZIndex: maxZ + 1 + i,
    }));

    commandStack.execute(new ReorderObjectCommand(canvas, changes));
    applyLayerOrder(canvas);
    // Discard and re-select to force Fabric to render the new z-order immediately
    // (without this, the active-selection overlay masks the change until deselect)
    canvas.discardActiveObject();
    canvas.renderAll();
    canvas.setActiveObject(active);
    canvas.renderAll();
  };

  /**
   * Send the selected object(s) to back within their layer.
   */
  const sendToBack = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const active = canvas.getActiveObject();
    if (!active) return;

    const targets = active.type === 'activeselection'
      ? active.getObjects()
      : [active];

    // Give targets zIndex 0, shift existing objects up
    const targetIds = new Set(targets.map((o) => o));

    const nonGridObjects = canvas.getObjects().filter(
      (o) => !o.data?.isGridLine && !o.data?.isSnapGuide && !targetIds.has(o)
    );

    // Shift non-target objects up by targets.length
    const otherChanges = nonGridObjects.map((obj) => ({
      object: obj,
      oldZIndex: obj.data?.zIndexWithinLayer ?? 0,
      newZIndex: (obj.data?.zIndexWithinLayer ?? 0) + targets.length,
    }));

    const targetChanges = targets.map((obj, i) => ({
      object: obj,
      oldZIndex: obj.data?.zIndexWithinLayer ?? 0,
      newZIndex: i,
    }));

    commandStack.execute(
      new ReorderObjectCommand(canvas, [...targetChanges, ...otherChanges])
    );
    applyLayerOrder(canvas);
    // Discard and re-select to force Fabric to render the new z-order immediately
    canvas.discardActiveObject();
    canvas.renderAll();
    canvas.setActiveObject(active);
    canvas.renderAll();
  };

  /**
   * Group selected objects (ActiveSelection → fabric.Group).
   * Uses CompoundCommand for undo/redo support.
   */
  const group = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const active = canvas.getActiveObject();
    if (!active || active.type !== 'activeselection') return;

    const objects = active.getObjects();
    if (objects.length < 2) return;

    // Inherit layer from first object
    const inheritedLayer = objects[0].data?.layer;

    // Compute max zIndex within inherited layer
    const nonGridObjects = canvas.getObjects().filter(
      (o) => !o.data?.isGridLine && !o.data?.isSnapGuide
    );
    const maxZ = nonGridObjects.reduce(
      (max, o) => Math.max(max, o.data?.zIndexWithinLayer ?? 0),
      -1
    );

    // Delete commands for each original object
    const deleteCommands = objects.map(
      (obj) => new DeleteObjectCommand(canvas, obj)
    );

    // Create the group object
    const fabricGroup = new Group(objects, {
      originX: 'left',
      originY: 'top',
      data: {
        type: 'group',
        layer: inheritedLayer,
        zIndexWithinLayer: maxZ + 1,
        // Store original layers for ungroup restoration
        originalObjectLayers: objects.map((obj) => ({
          layer: obj.data?.layer,
          zIndexWithinLayer: obj.data?.zIndexWithinLayer,
        })),
      },
    });

    const createGroupCommand = new CreateObjectCommand(canvas, fabricGroup);

    const compound = new CompoundCommand(
      [...deleteCommands, createGroupCommand],
      'Gruppieren'
    );

    canvas.discardActiveObject();
    commandStack.execute(compound);
    canvas.setActiveObject(fabricGroup);
    canvas.renderAll();
  };

  /**
   * Ungroup a fabric.Group into individual objects.
   * Delegates to UngroupObjectsCommand for correct coordinate handling and undo/redo.
   */
  const ungroup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const active = canvas.getActiveObject();
    if (!active || active.type !== 'group') return;

    const cmd = new UngroupObjectsCommand(canvas, active);
    commandStack.execute(cmd);
  };

  /**
   * Delete the currently selected object(s).
   * Uses DeleteObjectCommand (single) or CompoundCommand (multi).
   */
  const deleteSelected = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const active = canvas.getActiveObject();
    if (!active) return;

    if (active.type === 'activeselection') {
      const objects = active.getObjects();
      const commands = objects.map((obj) => new DeleteObjectCommand(canvas, obj));
      canvas.discardActiveObject();
      commandStack.execute(new CompoundCommand(commands, 'Löschen'));
    } else {
      commandStack.execute(new DeleteObjectCommand(canvas, active));
    }

    canvas.renderAll();
  };

  /**
   * @returns {boolean} true if clipboard has content
   */
  const hasClipboard = () => clipboardRef.current !== null;

  return {
    copy,
    paste,
    duplicate,
    bringToFront,
    sendToBack,
    group,
    ungroup,
    deleteSelected,
    hasClipboard,
  };
}

export default useContextMenuActions;
